import React, { useMemo } from 'react';
import { HazardData, RiskConstructionData, WorkEnvironmentRiskData } from '../../../Helpers/utils';
import { AlertTriangle } from 'lucide-react';

interface RisksOverviewListProps {
    hazards: HazardData[];
    riskConstructions: RiskConstructionData[];
    workEnvironmentRisks: WorkEnvironmentRiskData[];
    selectedKey: string | null;
    onComponentClick: (key: string) => void;
    onRoomClick: (roomKey: string) => void;
}

// ── Risk Type Badge ─────────────────────────────────────────────

export type RiskBadgeType = 'hazard' | 'riskConstruction' | 'workEnvironment';

const BADGE_CONFIG: Record<RiskBadgeType, { label: string; color: string; bg: string; border: string }> = {
    hazard: { label: 'Miljö/hälsofara', color: '#991b1b', bg: '#fef2f2', border: '#dc2626' },
    riskConstruction: { label: 'Riskkonstruktion', color: '#8c4b22', bg: '#fcf5f2', border: '#ca6d31' },
    workEnvironment: { label: 'Arbetsmiljörisk', color: '#8c6e22', bg: '#fcf8f2', border: '#caa031' },
};

export const RiskBadge: React.FC<{ type: RiskBadgeType }> = ({ type }) => {
    const config = BADGE_CONFIG[type];
    return (
        <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '4px',
            fontSize: '11px',
            fontWeight: 600,
            padding: '2px 8px',
            borderRadius: '4px',
            color: config.color,
            backgroundColor: config.bg,
            border: `1px solid ${config.border}`,
            whiteSpace: 'nowrap',
        }}>
            <AlertTriangle size={11} />
            {config.label}
        </span>
    );
};

// ── List Item ───────────────────────────────────────────────────

export const ListItem: React.FC<{
    label: string;
    subLabel?: string;
    badgeType: RiskBadgeType;
    isSelected: boolean;
    onClick: () => void;
}> = ({ label, subLabel, badgeType, isSelected, onClick }) => (
    <div
        onClick={onClick}
        style={{
            padding: '10px 12px',
            borderRadius: '6px',
            cursor: 'pointer',
            marginBottom: '4px',
            backgroundColor: isSelected ? 'rgba(54, 158, 195, 0.08)' : 'white',
            border: isSelected ? '1px solid rgba(54, 158, 195, 0.3)' : '1px solid #e9ecef',
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '8px',
            transition: 'background-color 0.15s',
        }}
        onMouseEnter={(e) => {
            if (!isSelected) e.currentTarget.style.backgroundColor = '#f8f9fa';
        }}
        onMouseLeave={(e) => {
            if (!isSelected) e.currentTarget.style.backgroundColor = isSelected ? 'rgba(54, 158, 195, 0.08)' : 'white';
        }}
    >
        <div style={{ minWidth: 0 }}>
            <span style={{ fontWeight: 500, fontSize: '14px' }}>{label}</span>
            {subLabel && (
                <span style={{ display: 'block', color: '#666', fontSize: '13px', marginTop: '2px' }}>
                    {subLabel}
                </span>
            )}
        </div>
        <RiskBadge type={badgeType} />
    </div>
);

// ── Section Header ──────────────────────────────────────────────

export const RiskSectionHeader: React.FC<{
    title: string;
    count: number;
}> = ({ title, count }) => (
    <h5 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: '#444' }}>
        {title} ({count})
    </h5>
);

// ── Main List Component ─────────────────────────────────────────

const RisksOverviewList: React.FC<RisksOverviewListProps> = ({
    hazards,
    riskConstructions,
    workEnvironmentRisks,
    selectedKey,
    onComponentClick,
    onRoomClick,
}) => {
    // Group hazards by component key so a component with multiple hazard types
    // appears as a single list item with combined descriptions.
    const groupedHazards = useMemo(() => {
        const map = new Map<string, { label: string; descriptions: string[] }>();
        hazards.forEach(hazardData => {
            hazardData.components.forEach(component => {
                if (!map.has(component.key)) {
                    map.set(component.key, { label: component.label || component.type, descriptions: [] });
                }
                const desc = `${hazardData.hazardType}${component.hazardState ? ` — ${component.hazardState}` : ''}`;
                map.get(component.key)!.descriptions.push(desc);
            });
        });
        return Array.from(map.entries()).map(([key, { label, descriptions }]) => ({
            key, label, subLabel: descriptions.join(', '),
        }));
    }, [hazards]);

    // Same for risk constructions.
    const groupedRisks = useMemo(() => {
        const map = new Map<string, { label: string; riskTypes: string[] }>();
        riskConstructions.forEach(riskData => {
            riskData.components.forEach(component => {
                if (!map.has(component.key)) {
                    map.set(component.key, { label: component.label || component.type, riskTypes: [] });
                }
                map.get(component.key)!.riskTypes.push(riskData.riskType);
            });
        });
        return Array.from(map.entries()).map(([key, { label, riskTypes }]) => ({
            key, label, subLabel: riskTypes.join(', '),
        }));
    }, [riskConstructions]);

    const hasAny = groupedHazards.length > 0 || groupedRisks.length > 0 || workEnvironmentRisks.length > 0;

    if (!hasAny) {
        return (
            <div style={{ padding: '40px', textAlign: 'center', color: '#999' }}>
                <p style={{ margin: 0 }}>Inga risker registrerade</p>
            </div>
        );
    }

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            {groupedHazards.length > 0 && (
                <div>
                    <RiskSectionHeader title="Miljö/hälsofara" count={hazards.length} />
                    <div style={{ marginTop: '8px' }}>
                        {groupedHazards.map(item => (
                            <ListItem
                                key={item.key}
                                label={item.label}
                                subLabel={item.subLabel}
                                badgeType="hazard"
                                isSelected={selectedKey === item.key}
                                onClick={() => onComponentClick(item.key)}
                            />
                        ))}
                    </div>
                </div>
            )}

            {groupedRisks.length > 0 && (
                <div>
                    <RiskSectionHeader title="Riskkonstruktioner" count={riskConstructions.length} />
                    <div style={{ marginTop: '8px' }}>
                        {groupedRisks.map(item => (
                            <ListItem
                                key={item.key}
                                label={item.label}
                                subLabel={item.subLabel}
                                badgeType="riskConstruction"
                                isSelected={selectedKey === item.key}
                                onClick={() => onComponentClick(item.key)}
                            />
                        ))}
                    </div>
                </div>
            )}

            {workEnvironmentRisks.length > 0 && (
                <div>
                    <RiskSectionHeader title="Arbetsmiljörisker" count={workEnvironmentRisks.length} />
                    <div style={{ marginTop: '8px' }}>
                        {workEnvironmentRisks.map((risk) => {
                            const preview = risk.description.length > 80
                                ? risk.description.substring(0, 80) + '...'
                                : risk.description;

                            return (
                                <ListItem
                                    key={risk.roomKey}
                                    label={risk.roomLabel}
                                    subLabel={preview}
                                    badgeType="workEnvironment"
                                    isSelected={selectedKey === risk.roomKey}
                                    onClick={() => onRoomClick(risk.roomKey)}
                                />
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

export default RisksOverviewList;
